import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, FormControl, InputAdornment, InputLabel, ListItemText, ListSubheader, MenuItem, OutlinedInput, Select, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TickIcon from 'src/assets/images/icons/tick-white.svg';
import IconInfoCircle from 'src/presentations/components/atom/icons/InfoCircle';
import IconCheckCircle from 'src/presentations/components/atom/icons/CheckCircle';
import WarningIcon from 'src/assets/images/icons/danger-light.svg';

import { Shipment } from 'src/generated/services/TFinancialApi';

// Keep the original IOption interface for compatibility
export interface IOption {
  isLabel?: boolean;
  shipmentNumber?: string;
  value: any;
  label: ReactNode;
  valueRender: ReactNode;
}

// Define option types for better type safety
export type OptionType = 'ACTION_NEEDED' | 'PENDING' | 'RESOLVED' | 'SHIPMENTS' | 'PENDING_RESOLVED';

// Extended option interface with raw data for internal use
export interface IShipmentOption extends IOption {
  // Additional raw data fields
  type?: OptionType;
  title?: string;
  status?: string;
  deviationCount?: number;
  approvalCount?: number;
  isNoRateCard?: boolean;
  itemsCount?: number;
  itemsTotal?: number;
}

export interface ShipmentData {
  ACTION_NEEDED?: Shipment[];
  PENDING?: Shipment[];
  RESOLVED?: Shipment[];
  PROCESSING?: Shipment[];
  UNDER_REVIEW?: Shipment[];
}

interface Props {
  shipmentList: ShipmentData;
  shipmentInvoiceId: string | string[];
  onSelectShipment: (value: string | string[]) => void;
  isProcurement?: boolean;
  isOperation?: boolean;
  disabled?: boolean;
  multiple?: boolean;
}

const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiFormControl-root': {
    minWidth: '290px',
    width: '100%',
    [theme.breakpoints.down(1520)]: {
      minWidth: '240px',
    },
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
  },
  '& MuiList-root': {
    boxShadow: '0px 12px 12px 0px rgba(34, 65, 102, 0.09)',
  },
}));

export const ShipmentSelect = ({
  shipmentList,
  shipmentInvoiceId,
  onSelectShipment,
  isProcurement = false,
  isOperation = false,
  disabled = false,
  multiple = false,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const onSelectAll = useCallback(
    (option: IShipmentOption) => {
      if (multiple && Array.isArray(shipmentInvoiceId)) {
        const sourceItems =
          option.type === 'PENDING_RESOLVED' ? [...(shipmentList['PENDING'] ?? []), ...(shipmentList['RESOLVED'] ?? [])] : shipmentList[option.type] ?? [];
        const shipmentIds = sourceItems.map((shipment: Shipment) => shipment.shipmentInvoiceId);
        const newItems = [...new Set([...shipmentInvoiceId, ...shipmentIds])];
        onSelectShipment(newItems);
      }
    },
    [multiple, shipmentInvoiceId, onSelectShipment, shipmentList]
  );

  const onSelectClearAll = useCallback(
    (option: IShipmentOption) => {
      if (multiple && Array.isArray(shipmentInvoiceId)) {
        const sourceItems =
          option.type === 'PENDING_RESOLVED' ? [...(shipmentList['PENDING'] ?? []), ...(shipmentList['RESOLVED'] ?? [])] : shipmentList[option.type] ?? [];
        const shipmentIds = sourceItems.map((shipment: Shipment) => shipment.shipmentInvoiceId);
        const newItems = shipmentInvoiceId.filter((id) => !shipmentIds.includes(id));
        onSelectShipment(newItems);
      }
    },
    [multiple, shipmentInvoiceId, onSelectShipment, shipmentList]
  );

  // Render section header
  const renderSectionHeader = useCallback(
    (option: IShipmentOption) => {
      // Compute group items based on option type
      let groupItems: Shipment[] = [];
      if (option.type === 'PENDING_RESOLVED') {
        groupItems = [...(shipmentList['PENDING'] ?? []), ...(shipmentList['RESOLVED'] ?? [])];
      } else if (option.type === 'SHIPMENTS') {
        groupItems = shipmentList['RESOLVED'] ?? [];
      } else if (option.type) {
        groupItems = shipmentList[option.type as keyof ShipmentData] ?? [];
      }
      const groupTotal = groupItems.length;
      const groupIds = groupItems.map((s) => s.shipmentInvoiceId);
      const selectedCount = Array.isArray(shipmentInvoiceId) ? shipmentInvoiceId.filter((id) => groupIds.includes(id)).length : 0;

      return (
        <div className='flex items-center gap-8 text-neutral-50'>
          <div className='uppercase text-12'>{option.title}</div>
          <div className='ml-auto flex items-center gap-8'>
            {option.type === 'ACTION_NEEDED' && <div className='text-10'>{isProcurement ? 'Procurement Review' : 'Logistics Review'}</div>}
            {option.type === 'PENDING_RESOLVED' && <div className='text-10'>{isProcurement ? 'Logistics Review' : 'Procurement Review'}</div>}
            <div className='flex items-center gap-16'>
              <div className='rounded-full text-10 bg-neutral-10 text-neutral-80 py-4 px-6 leading-normal'>
                {groupTotal > 0 && (
                  <>
                    {selectedCount} of {groupTotal}
                  </>
                )}
              </div>
              {multiple && (
                <div className='flex items-center gap-8'>
                  <button type='button' className='text-[11px] text-primary' onClick={() => onSelectAll(option)}>
                    Select All
                  </button>
                  <div className='w-1 h-10 bg-neutral-20' />
                  <button type='button' className='text-[11px] text-red-50' onClick={() => onSelectClearAll(option)}>
                    Clear All
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    },
    [isProcurement, multiple, shipmentInvoiceId, shipmentList, onSelectAll, onSelectClearAll]
  );

  // Helper function to render the icon based on shipment status and type
  const renderShipmentIcon = useCallback((type: OptionType | undefined, status: string | undefined) => {
    if (type === 'RESOLVED' && status === 'APPROVED') {
      return (
        <div className='w-20 h-20 rounded-full bg-[#00992B] relative'>
          <IconCheckCircle size={24} color='#CCEBD5' className='absolute -left-2 -top-2' />
        </div>
      );
    }

    if (type === 'RESOLVED' && status === 'REJECTED') {
      return <img src={WarningIcon} alt='' />;
    }

    return (
      <div className='w-20 h-20 rounded-full bg-[#606060] relative'>
        <IconInfoCircle size={24} color='#B9BABC' className='absolute -left-2 -top-2' />
      </div>
    );
  }, []);

  // Helper function to generate the types text
  const getTypesText = useCallback((option: IShipmentOption) => {
    const types = [];
    if (option.deviationCount && option.deviationCount > 0) {
      types.push(`${option.deviationCount} Deviation${option.deviationCount > 1 ? 's' : ''}`);
    }
    if (option.approvalCount && option.approvalCount > 0) {
      types.push(`${option.approvalCount} Approval${option.approvalCount > 1 ? 's' : ''}`);
    }
    if (option.isNoRateCard) {
      types.push('NRCF');
    }
    return types.join(' - ');
  }, []);

  // Helper function to highlight text based on search term
  const highlightText = useCallback((text: string, keyword: string): React.ReactNode => {
    if (!keyword || !text) return text;

    // Create a regex that matches the exact keyword, not partial matches
    const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'g');
    const parts = text.split(regex);

    // Generate a stable key for highlighted parts
    let highlightCount = 0;

    return parts.map((part) => {
      if (part === keyword) {
        // Use a more stable key that doesn't rely on array index
        const uniqueKey = `highlight-${keyword}-${highlightCount}`;
        // Increment counter for next highlight
        highlightCount += 1;

        return (
          <span key={uniqueKey} className='bg-[#FFFEC8] font-medium'>
            {part}
          </span>
        );
      }
      return part;
    });
  }, []);

  // Render shipment option with optional highlighting
  const renderShipmentOption = useCallback(
    (option: IShipmentOption, searchTerm: string = '') => {
      const typesText = getTypesText(option);
      const icon = renderShipmentIcon(option.type, option.status);

      // Render the shipment number with optional highlighting
      const shipmentNumber = option.shipmentNumber || '';
      let highlightedNumber: React.ReactNode = shipmentNumber;

      // Only highlight exact matches of the search term within the shipment number
      if (searchTerm.trim()) {
        highlightedNumber = highlightText(shipmentNumber, searchTerm);
      }

      return (
        <div className='flex items-center gap-[8px]'>
          {icon}
          <div>
            <p className='text-12 xl:text-16'>{highlightedNumber}</p>
            <p className='text-12 text-[#808080]'>{typesText}</p>
          </div>
        </div>
      );
    },
    [getTypesText, renderShipmentIcon, highlightText]
  );

  // Render the value shown in the select input when closed
  const renderSelectedValue = useCallback(
    (option: IShipmentOption) => {
      if (!option) return null;

      const icon = renderShipmentIcon(option.type, option.status);

      return (
        <div className='flex gap-6 xl:gap-8 items-center'>
          {icon}
          <div className='flex items-center gap-4 xl:gap-6'>
            <p className='text-12 xl:text-14'>{option.shipmentNumber}</p>
            {(option.type === 'PENDING' || option.type === 'ACTION_NEEDED') && <div className='w-4 h-4 rounded-full bg-[#DFDFDF]' />}
            {option.type === 'ACTION_NEEDED' && (
              <p className='text-[11px] xl:text-12 text-neutral-50'>{isProcurement ? 'Procurement Review' : 'Logistics Review'}</p>
            )}
            {option.type === 'PENDING' && <p className='text-[11px] xl:text-12 text-neutral-50'>{isProcurement ? 'Logistics Review' : 'Procurement Review'}</p>}
          </div>
        </div>
      );
    },
    [renderShipmentIcon, isProcurement]
  );

  const getOptionLabel = useCallback(
    (name: string, title: string, itemsCount?: number, itemsTotal?: number): IShipmentOption => {
      const option: IShipmentOption = {
        isLabel: true,
        value: name,
        type: name as OptionType,
        title,
        itemsCount,
        itemsTotal,
        // Keep the original label and valueRender for compatibility
        label: null as unknown as ReactNode,
        valueRender: null as unknown as ReactNode,
      };

      // Set the rendered label
      option.label = renderSectionHeader(option);

      return option;
    },
    [renderSectionHeader]
  );

  const getOptionData = useCallback(
    (data: Shipment, type: string): IShipmentOption => {
      const isLocalActionNoRateCard = data?.status === 'FLAGGED' && data?.currentDeviationWorkflow === 'PROCUREMENT_NO_CONTRACT_REVIEW';

      // Create the option with raw data
      const option: IShipmentOption = {
        value: data.shipmentInvoiceId,
        shipmentNumber: data.shipmentNumber,
        type: type as OptionType,
        status: data.status,
        deviationCount: data.numberOfDeviations,
        approvalCount: data.numberOfApprovals,
        isNoRateCard: isLocalActionNoRateCard,
        // Keep the original label and valueRender for compatibility
        label: null as unknown as ReactNode,
        valueRender: null as unknown as ReactNode,
      };

      // Set the rendered elements
      option.label = renderShipmentOption(option);
      option.valueRender = renderSelectedValue(option);

      return option;
    },
    [renderShipmentOption, renderSelectedValue]
  );

  const groupingNrcfItems = useCallback((items: Shipment[]) => {
    const nrcfItems = items.filter((item) => item?.currentDeviationWorkflow === 'PROCUREMENT_NO_CONTRACT_REVIEW');
    const nonNrcfItems = items.filter((item) => item?.currentDeviationWorkflow !== 'PROCUREMENT_NO_CONTRACT_REVIEW');
    return [...nrcfItems, ...nonNrcfItems];
  }, []);

  const options: IOption[] = useMemo(() => {
    const items = shipmentList;
    let servicesListOptions = [];

    const actionNeededItems = items.ACTION_NEEDED && items.ACTION_NEEDED?.length > 0 ? groupingNrcfItems(items.ACTION_NEEDED) : [];
    const pendingItems = items.PENDING && items.PENDING?.length > 0 ? groupingNrcfItems(items.PENDING) : [];
    const resolvedItems = items.RESOLVED && items.RESOLVED?.length > 0 ? groupingNrcfItems(items.RESOLVED) : [];
    const totalItems = actionNeededItems.length + pendingItems.length + resolvedItems.length;
    const processingItems = items.PROCESSING && items.PROCESSING?.length > 0 ? groupingNrcfItems(items.PROCESSING) : [];
    const underReviewItems = items.UNDER_REVIEW && items.UNDER_REVIEW?.length > 0 ? groupingNrcfItems(items.UNDER_REVIEW) : [];

    if (actionNeededItems?.length < 1 && pendingItems?.length < 1 && processingItems?.length < 1 && underReviewItems?.length < 1 && resolvedItems?.length > 0) {
      servicesListOptions = [
        ...servicesListOptions,
        getOptionLabel('SHIPMENTS', 'Shipments', resolvedItems.length, totalItems),
        ...resolvedItems.map((item) => getOptionData(item, 'RESOLVED')),
      ];
    } else {
      if (actionNeededItems?.length > 0) {
        servicesListOptions = [
          ...servicesListOptions,
          getOptionLabel('ACTION_NEEDED', 'Action Needed', actionNeededItems.length, totalItems),
          ...actionNeededItems.map((item) => getOptionData(item, 'ACTION_NEEDED')),
        ];
      }

      if (pendingItems?.length > 0 || resolvedItems?.length > 0) {
        servicesListOptions = [
          ...servicesListOptions,
          getOptionLabel('PENDING_RESOLVED', 'Pending/Resolved', pendingItems.length + resolvedItems.length, totalItems),
        ];
      }

      if (pendingItems?.length > 0) {
        servicesListOptions = [...servicesListOptions, ...pendingItems.map((item) => getOptionData(item, 'PENDING'))];
      }

      if (resolvedItems?.length > 0) {
        servicesListOptions = [...servicesListOptions, ...resolvedItems.map((item) => getOptionData(item, 'RESOLVED'))];
      }
      if (processingItems?.length > 0) {
        servicesListOptions = [
          ...servicesListOptions,
          getOptionLabel('PROCESSING', 'Processing', processingItems.length, totalItems),
          ...processingItems.map((item) => getOptionData(item, 'PROCESSING')),
        ];
      }

      if (underReviewItems?.length > 0) {
        servicesListOptions = [
          ...servicesListOptions,
          getOptionLabel('UNDER_REVIEW', 'Under Review', underReviewItems.length, totalItems),
          ...underReviewItems.map((item) => getOptionData(item, 'UNDER_REVIEW')),
        ];
      }
    }

    return servicesListOptions;
  }, [shipmentList, getOptionData, getOptionLabel, groupingNrcfItems]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isProcurement, multiple, shipmentInvoiceId, shipmentList];

  const totalOptions = options?.filter((item) => !item?.isLabel)?.length ?? 0;

  // Helper function to find the section header for a given option
  const findSectionHeader = (option: IOption, allOptions: IOption[]) => {
    const sectionIndex = allOptions.findIndex(
      (item, index) => item.isLabel && index < allOptions.indexOf(option) && !allOptions.slice(index + 1, allOptions.indexOf(option)).some((i) => i.isLabel)
    );
    return sectionIndex !== -1 ? allOptions[sectionIndex] : null;
  };

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;

    const searchTermLower = searchTerm.toLowerCase();
    const result = [];
    const sectionsWithMatches = new Set();

    // First pass: identify sections with matches
    options.forEach((option) => {
      if (option.isLabel) return;
      const shipmentNumber = option.shipmentNumber?.toLowerCase() || '';

      if (shipmentNumber.includes(searchTermLower)) {
        const sectionHeader = findSectionHeader(option, options);
        if (sectionHeader) {
          sectionsWithMatches.add(sectionHeader.value);
        }
      }
    });

    // Second pass: build filtered list
    options.forEach((option) => {
      if (option.isLabel) {
        if (sectionsWithMatches.has(option.value)) {
          result.push(option);
        }
      } else {
        const shipmentNumber = option.shipmentNumber?.toLowerCase() || '';
        if (shipmentNumber.includes(searchTermLower)) {
          // Create new option with highlighted text
          const updatedOption = { ...option };
          if (updatedOption.shipmentNumber) {
            const shipmentOption = option as IShipmentOption;
            updatedOption.label = renderShipmentOption(shipmentOption, searchTerm);
          }
          result.push(updatedOption);
        }
      }
    });

    return result;
  }, [options, searchTerm, renderShipmentOption]);

  if (totalOptions < 1) {
    return null;
  }

  return (
    <StyledBox>
      <FormControl size='small'>
        <InputLabel>Select Shipment</InputLabel>

        <Select
          disabled={disabled}
          multiple={multiple}
          onChange={(event) => onSelectShipment(event.target.value)}
          value={shipmentInvoiceId}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
          onClose={() => {
            // Reset search term when dropdown is closed
            setSearchTerm('');
          }}
          input={<OutlinedInput label='View Shipment' />}
          renderValue={(selected) => {
            if (!multiple) {
              const selectedOption = options?.find((option) => option.value === selected);
              return <div className='relative'>{selectedOption?.valueRender}</div>;
            }

            // For multiple selection
            const selectedValues = selected as string[];
            const selectedOptions = options?.filter((option) => selectedValues.includes(option.value));

            return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selectedOptions?.length ?? 0} Shipments Selected</Box>;
          }}
        >
          <div className='sticky top-0 z-10 px-8 xl:px-12 pt-4 pb-12 mb-4 border-b border-neutral-20 bg-neutral-10 rounded-t-6'>
            <TextField
              size='small'
              autoFocus
              placeholder='Search shipment'
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
                className: 'text-12 xl:text-16', // Responsive font size using Tailwind
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.75rem', xl: '1rem' }, // 12px on small, 16px on xl
                },
              }}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                // Prevent select from closing when typing
                if (e.key !== 'Escape' && e.key !== 'Tab') {
                  e.stopPropagation();
                }
              }}
            />
          </div>
          {filteredOptions?.map((option) => {
            if (option?.isLabel) {
              return (
                <ListSubheader key={option.value} disableSticky>
                  {option.label}
                </ListSubheader>
              );
            }
            const isSelected = multiple ? Array.isArray(shipmentInvoiceId) && shipmentInvoiceId.includes(option.value) : shipmentInvoiceId === option.value;
            return (
              <MenuItem key={option.value} value={option.value} style={{ paddingBlock: '10px', backgroundColor: 'transparent !important' }}>
                <div className={`w-22 h-22 rounded-6 border relative mr-12 ${isSelected ? 'border-primary' : 'border-neutral-40'}`}>
                  {isSelected && (
                    <div className='w-20 h-20 bg-primary flex items-center justify-center rounded-3'>
                      <img src={TickIcon} alt='TickIcon' />
                    </div>
                  )}
                </div>
                <ListItemText primary={option.label} sx={{ textTransform: 'capitalize' }} />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </StyledBox>
  );
};
